// The Cobrain mark: a brain drawn as one coiled line, which is the pun the name makes.
// Animated while loading, still otherwise. The gradient runs from the accent to the private
// colour of the U20 palette (parts/bar.html draws it so).
//
// The drawing is Mike's, unchanged in shape (U111, 2026-09-20: "I like it the way it is, but
// the proportions are wrong"). What changed is how it sits in its box and how it is stroked:
//
//  - The art now fills the square. It used to occupy about 16 of every 26 pixels, because the
//    spinal tail hung below it and pushed the brain up and small, so `size={26}` in the top bar
//    delivered a 16px mark. The tail is gone and the brain is scaled to the box, so a size is
//    now the size you get.
//  - The stroke weight is chosen from the size. One weight over a 16-to-120px range means
//    sub-pixel strokes at the small end: 5/120 of a 26px mark is 1.08 device pixels, which is
//    the grey fuzz the mark used to have in the bar and the tab.
//  - The inner folds are drawn at every size (U130). They were dropped below 34px on the theory
//    that five of them inside a 24px shape are noise, but the top bar draws the mark at 30 and the
//    tab at 16, so the two sizes Mike actually looks at all day were the outline and one vertical
//    line -- a cloud with a line through it, not a brain. What survives of the thinning is the
//    stroke ladder below, which is what stopped the folds turning to mush in the first place.
//  - One gradient across the whole mark, in user space. Under the default objectBoundingBox a
//    vertical path has a zero-width box, so the fissure resolved to nothing and every fold ran
//    the full orange-to-violet inside its own few pixels.
//  - The gradient id is per instance. It was the literal "g", so a page showing more than one
//    logo had several defs claiming the same id and every reference resolved to the first.
import { useId } from 'react';

/** The art's drawn height as a fraction of the square box, which is what a lockup aligns to. */
export const MARK_ART = 0.767;

/** Rendered stroke weight on the 120 grid. Small marks need a heavier line, not a scaled one. */
function weightFor(size: number): number {
  if (size <= 20) return 11;
  if (size <= 32) return 9.5;
  if (size <= 48) return 8;
  return 7;
}

export function Logo({ size = 120, spinning = false }: { size?: number; spinning?: boolean }) {
  const gid = `cb-${useId().replace(/:/g, '')}`;
  const w = weightFor(size) / 1.18; // the group is scaled, so the authored width is divided
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" role="img" aria-label="Cobrain" className={spinning ? 'logo spinning' : 'logo'}>
      <defs>
        <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1="10" y1="12" x2="110" y2="108">
          <stop offset="0" stopColor="#ff8a5b" />
          <stop offset="1" stopColor="#7c6cff" />
        </linearGradient>
      </defs>
      <g transform="translate(60,60) scale(1.18) translate(-60,-54)"
        fill="none" stroke={`url(#${gid})`} strokeLinecap="round" strokeLinejoin="round">
        <path
          d="M60 14c-14 0-24 8-26 20-10 1-18 9-18 19 0 7 4 13 10 16-1 12 8 22 20 22 5 0 9-2 12-5 3 3 7 5 12 5 12 0 21-10 20-22 6-3 10-9 10-16 0-10-8-18-18-19-2-12-12-20-26-20z"
          strokeWidth={w} />
        {/* The outline's bottom notch is at y=85.5 and its crown at y=14; the fissure is heavier
            than the old one, so its round caps are set inside both rather than at them. */}
        <path d="M60 18v64" strokeWidth={w * 0.78} />
        <path d="M40 40c6 4 8 10 6 18M80 40c-6 4-8 10-6 18" strokeWidth={w * 0.72} opacity="0.9" />
        <path d="M46 70c6-2 10 0 14 4M74 70c-6-2-10 0-14 4" strokeWidth={w * 0.72} opacity="0.9" />
      </g>
    </svg>
  );
}

// The lockup (U112): the mark and the word as one drawn thing rather than a label set beside a
// picture. The word was the UI's own heading face at the UI's own size with a flat 8px gap, so
// mark and word stood at the same optical height with no hierarchy between them, which is what
// reads as amateur. Here the word's cap height is locked to the mark's drawn height, the gap
// comes off the cap rather than a fixed pixel count, and the face is Sora (Mike's choice) so the
// logo is its own thing and not a run of the page's furniture.
const SORA_CAP = 0.7; // Sora's cap height in ems, which is what turns a cap target into a size

export function Brand({ size = 30, spinning = false }: { size?: number; spinning?: boolean }) {
  const cap = size * MARK_ART * 0.62;
  return (
    <span className="lockup" style={{ display: 'inline-flex', alignItems: 'center', gap: `${cap * 0.5}px` }}>
      <Logo size={size} spinning={spinning} />
      {/* Named so the bar's narrow-phone rule can leave the mark alone (styles.css; the mark keeps its own label). */}
      <span
        className="lockup-word"
        style={{
          fontFamily: "'Sora', 'Barlow', ui-sans-serif, system-ui, sans-serif",
          fontWeight: 700,
          fontSize: `${cap / SORA_CAP}px`,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          // line-height 1 centres the line box, not the capitals; Sora's ascent runs above the
          // cap line, so the word sits high against the mark without this.
          transform: `translateY(${cap * 0.055}px)`,
        }}
      >
        Cobrain
      </span>
    </span>
  );
}
